import React, { forwardRef } from 'react';
import ReactDOM from 'react-dom';
import styled, { ThemeProvider } from 'styled-components';
import { position } from 'styled-system';
import { themeGet } from '@styled-system/theme-get';
import posed, { PoseGroup } from 'react-pose';
import { theme } from '../themes/default';
import { Flex, Box } from '../grid/Grid';
import { Icon } from '../icon/Icon';
import iconSuccess from './success.svg';
import iconError from './error.svg';
import iconWarning from './warning.svg';

const PLACEMENT = {
  TOP_RIGHT: 'topRight',
  TOP_LEFT: 'topLeft',
  BOTTOM_RIGHT: 'bottomRight',
  BOTTOM_LEFT: 'bottomLeft',
};

const placementStyleMap = {
  topRight: { top: 10, right: 10 },
  topLeft: { top: 10, left: 10 },
  bottomRight: { bottom: 10, right: 10 },
  bottomLeft: { bottom: 10, left: 10 },
};

const getContainer = key => {
  const id = `notification-${key}`;
  const container = document.getElementById(id);
  if (container) {
    return container;
  } else {
    const div = document.createElement('div');
    div.id = id;
    document.body.appendChild(div);
    return div;
  }
};

const StyledNotification = styled.div`
  position: fixed;
  z-index: ${themeGet('zIndex.tooltip')};
  ${position}
`;

const Wrapper = styled(Box).attrs({
  display: 'flex',
  pl: 5,
  pr: 4,
  py: 4,
  mb: 5,
})`
  align-items: center;
  position: relative;
  width: ${themeGet('notification.width')};
  border-radius: ${themeGet('notification.borderRadius')};
  box-shadow: ${themeGet('notification.boxShadow')};
  background-color: ${themeGet('notification.backgroundColor')};
  transform-origin: top center;
`;

const StatusIcon = styled.img`
  width: 32px;
  height: 32px;
`;

const Title = styled(Box)`
  font-size: ${themeGet('notification.title.fontSize')};
  line-height: 1.5;
  font-weight: 600;
  color: ${themeGet('notification.title.color')};
`;

const Desc = styled(Box)`
  font-size: ${themeGet('notification.description.fontSize')};
  line-height: 1.5;
  color: ${themeGet('notification.description.color')};
`;

const CloseButton = styled(Icon).attrs({
  name: 'close',
})`
  position: absolute;
  top: ${themeGet('notification.closeButton.top')};
  right: ${themeGet('notification.closeButton.right')};
  font-size: ${themeGet('notification.closeButton.fontSize')};
  cursor: pointer;
`;

class NotificationCard extends React.PureComponent {
  closeTimer = null;

  componentDidMount() {
    this.startCloseTimer();
  }

  componentDidUpdate() {
    this.restartCloseTimer();
  }

  componentWillUnmount() {
    this.clearCloseTimer();
  }

  close = e => {
    const { onClose, cardKey } = this.props;

    if (e) {
      e.stopPropagation();
    }
    this.clearCloseTimer();
    onClose(cardKey);
  };

  startCloseTimer = () => {
    const { duration } = this.props.data;

    if (duration) {
      this.closeTimer = setTimeout(() => {
        this.close();
      }, duration);
    }
  };

  clearCloseTimer = () => {
    if (this.closeTimer) {
      clearTimeout(this.closeTimer);
      this.closeTimer = null;
    }
  };

  restartCloseTimer() {
    this.clearCloseTimer();
    this.startCloseTimer();
  }

  render() {
    const { message, description, icon } = this.props.data;
    return (
      <Wrapper ref={this.props.innerRef}>
        <StatusIcon src={icon} />
        <Flex flexDirection="column" justifyContent="space-between">
          <Box ml={5} mr={4}>
            <Title>{message}</Title>
            <Desc>{description}</Desc>
          </Box>
        </Flex>
        <CloseButton onClick={this.close} />
      </Wrapper>
    );
  }
}

const NotificationCardWithRef = forwardRef((props, ref) => <NotificationCard {...props} ref={ref} />);

const NotificationCardWithAnimation = posed(NotificationCardWithRef)({
  init: {
    opacity: 0,
    x: '100%',
  },
  enter: {
    opacity: 1,
    x: '0%',
    transition: { duration: 300, ease: 'easeIn' },
  },
  exit: {
    opacity: 0,
    marginBottom: '-50%',
    transition: { duration: 300, ease: 'easeOut' },
  },
});

class NotificationList extends React.Component {
  state = {
    data: new Map(),
  };

  add = config => {
    const key = config.key || Date.now();
    this.setState(state => {
      if (state.data.has(key)) {
        state.data.delete(key);
      }
      return state.data.set(key, config);
    });
  };

  remove = key => {
    this.setState(state => state.data.delete(key));
  };

  render() {
    const { data } = this.state;
    const { placement } = this.props;

    return (
      <StyledNotification {...placementStyleMap[placement]}>
        <PoseGroup preEnterPose="init">
          {Array.from(data).map(([key, value]) => (
            <NotificationCardWithAnimation key={key} cardKey={key} data={value} onClose={this.remove} />
          ))}
        </PoseGroup>
      </StyledNotification>
    );
  }
}
const cache = {};

NotificationList.getInstance = (placement, callback) => {
  if (cache[placement]) return callback(cache[placement]);
  const container = getContainer(placement);
  let called = false;

  ReactDOM.render(
    <ThemeProvider theme={theme}>
      <NotificationList
        placement={placement}
        ref={notification => {
          if (called) return;
          called = true;
          const result = {
            notice: noticeProps => {
              notification.add(noticeProps);
            },
            removeNotice: key => {
              notification.remove(key);
            },
            destory: () => {
              ReactDOM.unmountComponentAtNode(container);
            },
            component: notification,
          };
          cache[placement] = result;
          callback(result);
        }}
      />
    </ThemeProvider>,
    container
  );
};

const notice = ({
  key,
  message,
  description,
  placement = PLACEMENT.TOP_RIGHT,
  duration = 5000,
  icon = iconSuccess,
}) => {
  NotificationList.getInstance(placement, notification => {
    notification.notice({
      key,
      message,
      description,
      duration,
      icon,
    });
  });
};

export const Notification = {
  open: notice,
  success: config => notice({ ...config, icon: iconSuccess }),
  error: config => notice({ ...config, icon: iconError }),
  warning: config => notice({ ...config, icon: iconWarning }),
};
